import { z } from "zod";

/**
 * Shared DTO cho Impersonation read-only "View as user" (US-AE11.F3-0039 · @UC9102
 * read-only-impersonation). MỘT nguồn sự thật cho api (S01) + admin web (S03).
 *
 * "Xem như user" = admin ĐẶC QUYỀN (SUPER_ADMIN/OPS) xem ĐÚNG màn hình user để hỗ trợ, TUYỆT ĐỐI
 * KHÔNG ghi (tiền/governance/profile). Phiên ngắn hạn (TTL) + bằng chứng vận hành nặng.
 *
 * BẤT BIẾN (invariants):
 * - AC1: start BẮT BUỘC `reason` (min len) + `ticketId` — thiếu ⇒ reject (không tạo phiên).
 * - AC2/read-only: session resp `readOnly` LUÔN `true` (cứng) — client biết chế độ chỉ-đọc.
 * - AC3 audit nặng: reason/ticketId là bằng chứng vận hành — KHÔNG PII user thô.
 * - AC5: DTO KHÔNG khai báo token/secret thô. Token impersonation (nếu trả) là access token opaque
 *   để client gắn Bearer — KHÔNG log/redact-diff. Chỉ id + targetUserId + thời gian trong session state.
 * - `.strict()` cho body start — chống over-post/field lạ.
 */

// ============================================================
// Enum-lite — trạng thái vòng đời phiên impersonation
// ============================================================

/** Trạng thái phiên. active = đang xem; ended = admin thoát; expired = quá TTL (guard vô hiệu). */
export const impersonationStatusSchema = z.enum(["active", "ended", "expired"]);
export type ImpersonationStatus = z.infer<typeof impersonationStatusSchema>;

// ============================================================
// Start (POST /admin/users/:id/impersonate)
// ============================================================

export const IMPERSONATION_REASON_MIN_LENGTH = 4;
export const IMPERSONATION_REASON_MAX_LENGTH = 500;
export const IMPERSONATION_TICKET_MIN_LENGTH = 2;
export const IMPERSONATION_TICKET_MAX_LENGTH = 100;

/**
 * Body bắt đầu phiên "xem như user". reason + ticketId BẮT BUỘC (invariant story:1635 — không tạo
 * phiên thiếu bằng chứng). `.strict()` chặn field lạ. targetUserId đến từ path param (không trong body).
 * KHÔNG nhận TTL/expiresAt từ client (server áp TTL — chống client kéo dài phiên đặc quyền).
 */
export const startImpersonationSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(IMPERSONATION_REASON_MIN_LENGTH)
      .max(IMPERSONATION_REASON_MAX_LENGTH),
    ticketId: z
      .string()
      .trim()
      .min(IMPERSONATION_TICKET_MIN_LENGTH)
      .max(IMPERSONATION_TICKET_MAX_LENGTH),
  })
  .strict();

export type StartImpersonationBody = z.infer<typeof startImpersonationSchema>;

// ============================================================
// Session state (resp start + GET active session)
// ============================================================

/**
 * Trạng thái 1 phiên impersonation trả cho UI (render banner cảnh báo bền suốt phiên).
 * `readOnly` LUÔN true (cứng — UI defence-in-depth ẩn nút ghi; backend là nguồn chặn chính).
 * `token` = access token opaque (nếu có) để client gắn Bearer đọc màn hình user — KHÔNG bắt buộc trong
 * mọi context (GET active session không trả lại token). KHÔNG lộ secret/refresh token thô.
 */
export const impersonationSessionSchema = z
  .object({
    id: z.string(),
    targetUserId: z.string(),
    status: impersonationStatusSchema,
    readOnly: z.literal(true),
    startedAt: z.string(),
    expiresAt: z.string(),
    endedAt: z.string().nullable(),
    /** Access token impersonation (chỉ trả lúc START) — client gắn Bearer để xem màn hình user. */
    token: z.string().optional(),
  })
  .strict();

export type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;

/** Envelope GET active session: có phiên active hay không (banner render dựa vào `session`). */
export const activeImpersonationResponseSchema = z
  .object({
    session: impersonationSessionSchema.nullable(),
  })
  .strict();

export type ActiveImpersonationResponse = z.infer<typeof activeImpersonationResponseSchema>;
